export const AI_PLAN_FEATURES = [
  {
    key: "ai_explain",
    label: "AI Explanations",
    description: "Explain selected text and short study questions.",
  },
  {
    key: "ai_translate",
    label: "AI Translation",
    description: "Translate text and short passages in the reader.",
  },
  {
    key: "ai_retranslate",
    label: "Retranslate with Feedback",
    description: "Retry translations with guidance and refinement.",
  },
  {
    key: "ai_actions",
    label: "Study Actions",
    description: "Examples, redesigns, quizzes, and other structured help.",
  },
  {
    key: "guided_step_lessons",
    label: "Guided Step-by-Step Lessons",
    description: "Learn through one guided micro-step at a time with checks and lesson resume.",
  },
  {
    key: "ai_memory",
    label: "AI Memory",
    description: "Use memory and personalization settings in Toofi replies.",
  },
  {
    key: "ai_review_reports",
    label: "Review Reports",
    description: "Generate deeper review and study performance reports.",
  },
  {
    key: "workspace_toofi",
    label: "Workspace Toofi",
    description: "Use Toofi across dashboard and student workspace surfaces.",
  },
  {
    key: "reader_toofi",
    label: "Reader Toofi",
    description: "Use Toofi inside the PDF reader.",
  },
  {
    key: "chat_sync_resource",
    label: "Resource Chat Sync",
    description: "Sync reader/resource chat continuity across devices.",
  },
  {
    key: "chat_sync_workspace",
    label: "Workspace Chat Sync",
    description: "Sync workspace Toofi continuity across devices.",
  },
  {
    key: "study_plan_save",
    label: "Plan Save",
    description: "Save durable study plans from Toofi conversations.",
  },
  {
    key: "study_plan_refine",
    label: "Plan Refine",
    description: "Refine and continue saved study plans.",
  },
  {
    key: "advanced_replan",
    label: "Advanced Replan",
    description: "Use stronger recovery and replanning flows.",
  },
  {
    key: "extended_history",
    label: "Extended History",
    description: "Keep longer AI history and stronger continuity.",
  },
  {
    key: "premium_reasoning",
    label: "Premium Reasoning",
    description: "Use stronger reasoning on harder study tasks and selected premium moments.",
  },
  {
    key: "plan_checkins",
    label: "Plan Check-ins",
    description: "Return to saved plans for structured progress check-ins and follow-up.",
  },
  {
    key: "exam_mode",
    label: "Exam Mode",
    description: "Use intensive exam-period guidance, reprioritization, and recovery support.",
  },
  {
    key: "mastery_insights",
    label: "Mastery Insights",
    description: "See stronger weak-topic and concept-mastery guidance across study sessions.",
  },
  {
    key: "deep_review_generation",
    label: "Deep Review Generation",
    description: "Generate richer review packs, deeper study summaries, and more advanced revision help.",
  },
  {
    key: "long_context_analysis",
    label: "Long-Context Analysis",
    description: "Handle longer, more complex study analysis and cross-topic reasoning tasks.",
  },
] as const;

export type AiPlanFeature = (typeof AI_PLAN_FEATURES)[number];
export type AiPlanFeatureKey = (typeof AI_PLAN_FEATURES)[number]["key"];
export type AiPlanTier = "free" | "plus" | "pro";

export const ALL_AI_FEATURE_KEYS = AI_PLAN_FEATURES.map((feature) => feature.key);

export const PRO_ONLY_AI_FEATURE_KEYS = [
  "ai_review_reports",
  "advanced_replan",
  "premium_reasoning",
  "plan_checkins",
  "exam_mode",
  "mastery_insights",
  "deep_review_generation",
  "long_context_analysis",
] as const satisfies AiPlanFeatureKey[];

export const PLUS_CORE_AI_FEATURE_KEYS = [
  "ai_explain",
  "ai_translate",
  "ai_retranslate",
  "ai_actions",
  "guided_step_lessons",
  "ai_memory",
  "workspace_toofi",
  "reader_toofi",
  "chat_sync_resource",
  "chat_sync_workspace",
  "study_plan_save",
  "study_plan_refine",
  "extended_history",
] as const satisfies AiPlanFeatureKey[];

const FEATURE_DEFAULTS: Record<AiPlanTier, AiPlanFeatureKey[]> = {
  free: [
    "ai_explain",
    "ai_translate",
    "workspace_toofi",
    "reader_toofi",
  ],
  plus: [...PLUS_CORE_AI_FEATURE_KEYS],
  pro: [
    ...PLUS_CORE_AI_FEATURE_KEYS,
    ...PRO_ONLY_AI_FEATURE_KEYS,
  ],
};

export const getDefaultPlanFeatureKeys = (tier: AiPlanTier): AiPlanFeatureKey[] => FEATURE_DEFAULTS[tier];

export const normalizePlanFeatures = (raw?: string[] | null) => {
  if (!raw || raw.length === 0) return new Set<AiPlanFeatureKey>();
  return new Set<AiPlanFeatureKey>(
    raw.filter((value): value is AiPlanFeatureKey =>
      ALL_AI_FEATURE_KEYS.includes(value as AiPlanFeatureKey),
    ) as AiPlanFeatureKey[],
  );
};

export const getPlanFeatureEntries = (raw?: string[] | null) => {
  const enabled = normalizePlanFeatures(raw);
  return AI_PLAN_FEATURES.map((feature) => ({
    ...feature,
    enabled: enabled.has(feature.key),
  }));
};
